import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  domError,
  errorText,
  formatError,
  result,
  validation,
  validationError,
  withTimeout,
} from './shared.ts';

async function permissionState(name: 'camera' | 'microphone'): Promise<string> {
  try {
    const status = await navigator.permissions.query({
      name: name as PermissionName,
    });
    return status.state;
  } catch {
    return 'unsupported';
  }
}

async function collect(): Promise<RawData> {
  const cameraState = await permissionState('camera');
  const microphoneState = await permissionState('microphone');
  if (cameraState == 'prompt' || microphoneState == 'prompt') {
    return { cameraState, microphoneState };
  }
  try {
    const stream = await withTimeout(
      navigator.mediaDevices.getUserMedia({ audio: true, video: true }),
      3000,
      'Media request timed out',
    );
    const tracks = stream.getTracks().map((track) => ({
      kind: track.kind,
      label: track.label,
      enabled: track.enabled,
      muted: track.muted,
      readyState: track.readyState,
    }));
    for (const track of stream.getTracks()) {
      track.stop();
    }
    return {
      cameraState,
      microphoneState,
      tracks,
    };
  } catch (error) {
    const detail = domError(error);
    return {
      cameraState,
      microphoneState,
      errorName: detail.name,
      errorMessage: detail.message,
    };
  }
}

function validatePermission(rawData: RawData): ValidationResult {
  const reasons: string[] = [];
  if (rawData.tracks != null && !Array.isArray(rawData.tracks)) {
    reasons.push('Media tracks have an invalid shape');
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return `${String(rawData.cameraState)}/${String(rawData.microphoneState)}`;
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validatePermission(rawData);
}

export const permissionPolicyTest: TestDefinition = {
  id: 'permissionPolicy',
  title: 'Permission policy',
  category: 'shared',
  format,
  run,
  validate,
};
